/**
 * Mode `badge` du SDK widget Rintintin (Phase 3).
 *
 * Crée un bouton flottant en bas de page (position fixed) qui ouvre la popup
 * au clic. La popup elle-même est instanciée paresseusement au premier clic
 * pour ne pas créer d'overlay tant que l'utilisateur n'a pas interagi.
 */

import { hostPrefersDutch } from './hostLocale';
import { mountPopupWidget } from './popup';
import { ensureStylesInjected } from './styles';
import type {
  RintintinBookingBadgePosition,
  RintintinBookingInitOptions,
  RintintinBookingInstance,
} from './types';

/** Couleur par défaut quand ni `badgeColor` ni `accent` ne sont fournis. */
const DEFAULT_BADGE_COLOR = '#a5765c';

/** SVG calendrier inline — currentColor pour s'aligner sur la couleur du bouton. */
const CALENDAR_ICON_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
  '<rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>' +
  '<line x1="16" y1="2" x2="16" y2="6"/>' +
  '<line x1="8" y1="2" x2="8" y2="6"/>' +
  '<line x1="3" y1="10" x2="21" y2="10"/>' +
  '</svg>';

/** Normalise une couleur hex (accepte avec/sans #). Retourne `null` si invalide. */
function normalizeColor(raw: string | undefined): string | null {
  if (!raw) return null;
  const cleaned = raw.replace(/^#/, '').trim();
  if (/^[0-9A-Fa-f]{6}$/.test(cleaned)) return `#${cleaned.toLowerCase()}`;
  return null;
}

/**
 * Résout la couleur de fond du badge :
 *  1. `opts.badgeColor` si valide
 *  2. sinon `opts.accent` si valide
 *  3. sinon couleur par défaut `#a5765c`
 */
function resolveBadgeColor(opts: RintintinBookingInitOptions): string {
  return normalizeColor(opts.badgeColor) || normalizeColor(opts.accent) || DEFAULT_BADGE_COLOR;
}

/**
 * Crée le badge flottant + délègue à `mountPopupWidget` pour l'ouverture
 * (popup instanciée à la première ouverture, gardée en cache ensuite).
 */
export function mountBadgeWidget(options: RintintinBookingInitOptions): RintintinBookingInstance {
  ensureStylesInjected(document);

  const position: RintintinBookingBadgePosition =
    options.badgePosition === 'bottom-left' ? 'bottom-left' : 'bottom-right';
  const dutchHost = hostPrefersDutch();
  const label = options.badgeLabel ?? (dutchHost ? 'Boeken' : 'Réserver');
  const color = resolveBadgeColor(options);

  const badge = document.createElement('button');
  badge.type = 'button';
  badge.className = `rtt-rb-badge rtt-rb-badge--${position}`;
  badge.style.background = color;
  badge.setAttribute(
    'aria-label',
    label && label.trim()
      ? label
      : dutchHost
        ? 'Boekingsvenster openen'
        : 'Ouvrir la fenêtre de réservation'
  );

  const iconWrap = document.createElement('span');
  iconWrap.className = 'rtt-rb-badge-icon';
  iconWrap.innerHTML = CALENDAR_ICON_SVG;
  iconWrap.setAttribute('aria-hidden', 'true');
  badge.appendChild(iconWrap);

  if (label && label.trim()) {
    const labelEl = document.createElement('span');
    labelEl.className = 'rtt-rb-badge-label';
    labelEl.textContent = label;
    badge.appendChild(labelEl);
  }

  document.body.appendChild(badge);

  let popup: RintintinBookingInstance | null = null;
  let destroyed = false;

  function ensurePopup(): RintintinBookingInstance {
    if (popup) return popup;
    // On retire `trigger` / `triggerLabel` : le badge est notre seul déclencheur
    // (évite la collision avec l'auto-injection d'un bouton dans le body par la popup).
    const popupOpts: RintintinBookingInitOptions = {
      ...options,
      mode: 'popup',
      trigger: undefined,
      triggerLabel: undefined,
    };
    popup = mountPopupWidget(popupOpts);
    return popup;
  }

  function open(): void {
    if (destroyed) return;
    ensurePopup().open?.();
  }

  function close(): void {
    if (popup) popup.close?.();
  }

  badge.addEventListener('click', open);

  return {
    open,
    close,
    destroy() {
      if (destroyed) return;
      destroyed = true;
      badge.removeEventListener('click', open);
      if (badge.parentNode) {
        badge.parentNode.removeChild(badge);
      }
      if (popup) {
        popup.destroy();
        popup = null;
      }
    },
  };
}
